// LeaveRequestsPage.jsx
import { CheckIcon, ViewIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { approveOrDenyLeaveRequest, deleteLeaveRequest, getAllLeaveRequests } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import TitleHeader from '../../components/TitleHeader';
import { hasPermission } from '../../Utils';

const LeaveRequestsPage = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showDeleted, setShowDeleted] = useState(false);
    const [decisionNote, setDecisionNote] = useState('');
    const [decisionStatus, setDecisionStatus] = useState('approved');
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isLoadingAction, setIsLoadingAction] = useState(false);

    useEffect(() => {
        loadRequests();
    }, [showDeleted, dateFromFilter, dateToFilter]);

    const loadRequests = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await getAllLeaveRequests(showDeleted, {
                dateFrom: dateFromFilter,
                dateTo: dateToFilter
            });

            console.log("Results Details ", res)

            if (res.success) setRequests(res.data);
            else setError('Failed to fetch requests');
        } catch (e) {
            setError('Server error');
        }

        setLoading(false);
    };

    const clearDateFilters = () => {
        setDateFromFilter('');
        setDateToFilter('');
    };

    const handleDecision = async () => {
        if (!selected) return;
        setIsLoadingAction(true);

        try {
            const res = await approveOrDenyLeaveRequest(selected._id, {
                status: decisionStatus,
                reviewNote: decisionNote
            });

            if (res.success) {
                loadRequests();
                onClose();
            }
        } catch (err) {
            console.error(err);
        }

        setIsLoadingAction(false);
    };

    const handleDelete = async (id) => {
        if (!hasPermission('deleteLeaveRequest')) return;
        await deleteLeaveRequest(id);
        loadRequests();
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        const year = d.getUTCFullYear();
        return `${month}/${day}/${year}`;
    };

    if (loading) return <Loading />;

    return (
        !hasPermission("viewLeaveRequests") ?
            <AccessDenied msg={'You don\'t have access to view this'} />

            :

            <Box maxW="full" mx="auto">
                <VStack spacing={6} align="stretch">
                    <TitleHeader
                        title="Leave Requests"
                        buttonText={null}
                        secondButton
                        secondButtonText={showDeleted ? 'Show Active' : 'Show Deleted'}
                        secondButtonOnClick={() => setShowDeleted(!showDeleted)}
                    />

                    <Box>
                        <HStack spacing={4} align="end" flexWrap="wrap">
                            <FormControl maxW="220px">
                                <FormLabel fontSize="sm">From Date</FormLabel>
                                <Input
                                    type="date"
                                    size="sm"
                                    value={dateFromFilter}
                                    onChange={(e) => setDateFromFilter(e.target.value)}
                                />
                            </FormControl>

                            <FormControl maxW="220px">
                                <FormLabel fontSize="sm">To Date</FormLabel>
                                <Input
                                    type="date"
                                    size="sm"
                                    value={dateToFilter}
                                    onChange={(e) => setDateToFilter(e.target.value)}
                                />
                            </FormControl>

                            {(dateFromFilter || dateToFilter) && (
                                <Button size="sm" variant="outline" onClick={clearDateFilters}>
                                    Clear
                                </Button>
                            )}
                        </HStack>
                    </Box>

                    {error && (
                        <Text color="red.500" fontSize="sm">
                            {error}
                        </Text>
                    )}

                    {requests.length ? (
                        <Table size='sm'>
                            <Thead>
                                <Tr>
                                    <Th>Courier</Th>
                                    <Th>Date Range</Th>
                                    <Th>Type</Th>
                                    <Th>Status</Th>
                                    <Th>Created</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {requests.map(req => (
                                    <Tr key={req._id}>
                                        <Td>{req.courierId?.name}</Td>
                                        <Td>{formatDate(req.dateFrom)} - {formatDate(req.dateTo)}</Td>
                                        <Td>{req.type}</Td>
                                        <Td>
                                            <Badge colorScheme={req.status === 'approved' ? 'green' : req.status === 'denied' ? 'red' : 'yellow'}>
                                                {req.status}
                                            </Badge>
                                        </Td>
                                        <Td>{new Date(req.createdAt).toLocaleDateString()}</Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Tooltip label="View">
                                                    <Button size="xs" onClick={() => { setSelected(req); onOpen(); }}>
                                                        <ViewIcon />
                                                    </Button>
                                                </Tooltip>

                                                {hasPermission('approveDenyLeaveRequest') && req.status === 'pending' && (
                                                    <Tooltip label="Approve/Deny">
                                                        <Button size="xs" onClick={() => { setSelected(req); onOpen(); }}>
                                                            <CheckIcon />
                                                        </Button>
                                                    </Tooltip>
                                                )}

                                                {/* {hasPermission('deleteLeaveRequest') && !req.deletedAt && (
                                                    <Tooltip label="Delete">
                                                        <Button size="xs" onClick={() => handleDelete(req._id)} colorScheme="red"><DeleteIcon /></Button>
                                                    </Tooltip>
                                                )} */}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    ) : (
                        <AccessDenied msg={'No leave requests found'} />
                    )}

                    <Modal isOpen={isOpen} onClose={onClose} size="lg">
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>Leave Request Details</ModalHeader>
                            <ModalBody>
                                {selected && (
                                    <VStack spacing={3} align="start">
                                        <Text><b>Courier:</b> {selected.courierId?.name}</Text>
                                        <Text><b>Type:</b> {selected.type} {selected.otherReason && `(${selected.otherReason})`}</Text>
                                        <Text><b>From:</b> {new Date(selected.dateFrom).toLocaleDateString()}</Text>
                                        <Text><b>To:</b> {new Date(selected.dateTo).toLocaleDateString()}</Text>
                                        <Text><b>Reason:</b> {selected.reason || '-'}</Text>
                                        <Text><b>Initials:</b> {selected.employeeInitials}</Text>
                                        <Text><b>Signed Date:</b> {new Date(selected.employeeSignatureDate).toLocaleDateString()}</Text>
                                        <Divider />
                                        <Text><b>Status:</b> {selected.status}</Text>
                                        <Text><b>Reviewed By:</b> {selected.reviewedBy?.firstName || '-'}</Text>
                                        <Text><b>Review Note:</b> {selected.reviewNote || '-'}</Text>
                                    </VStack>
                                )}

                                {hasPermission('approveDenyLeaveRequest') && selected?.status === 'pending' && (
                                    <Box mt={4}>
                                        <FormControl mb={3}>
                                            <FormLabel>Decision</FormLabel>
                                            <Select value={decisionStatus} onChange={(e) => setDecisionStatus(e.target.value)}>
                                                <option value="approved">Approve</option>
                                                <option value="denied">Deny</option>
                                            </Select>
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel>Note</FormLabel>
                                            <Input value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
                                        </FormControl>
                                    </Box>
                                )}
                            </ModalBody>

                            {hasPermission('approveDenyLeaveRequest') && selected?.status === 'pending' && (
                                <ModalFooter>
                                    <Button onClick={handleDecision} isLoading={isLoadingAction} colorScheme='blue'>
                                        Submit Decision
                                    </Button>
                                    <Button onClick={onClose} ml={3}>
                                        Close
                                    </Button>
                                </ModalFooter>
                            )}
                        </ModalContent>
                    </Modal>
                </VStack>
            </Box>
    );
};

export default LeaveRequestsPage;